import { FC, ReactNode, useState, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ListIcon from "@mui/icons-material/List";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { ProductScoringSection } from "./criteria/ProductScoringSection";
import { AiBadge } from "./AiBadge";
import { useGenerateScoringProfile } from "@/apis/scoring/generateScoringProfile";
import {
  transformGeneratedProfile,
  isEmptyScoringTypeReturned,
} from "@/lib/scoring/utils";
import { safeParseFilters } from "@/lib/company-filter/utils/safeParse";
import { useFeatureFlag } from "@/hooks/useLaunchDarkly";
import { getAiScoringUrl } from "@/utils/module-urls";
import { getTenantId } from "@/utils/auth";
import type { ScoringType } from "@/lib/scoring/types";
import type { Segment } from "@/apis/bacchus/types";

type WizardStep = "choose" | "input" | "building";
type InputMode = "products" | "segment";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreateWithAi: () => void;
  onBuildManually: () => void;
  onGenerated: (criteria: ScoringType[], name: string) => void;
  segments?: Segment[];
}

interface MethodCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  badge?: ReactNode;
  testId: string;
  onClick: () => void;
  disabled?: boolean;
  /** Drops the bottom margin so the last card sits flush above the actions. */
  last?: boolean;
}

const MethodCard: FC<MethodCardProps> = ({
  icon,
  title,
  description,
  badge,
  testId,
  onClick,
  disabled = false,
  last = false,
}) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      p: 2,
      mb: last ? 0 : 1.5,
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "flex-start",
      gap: 2,
      width: "100%",
      textAlign: "left",
      opacity: disabled ? 0.5 : 1,
      "&:hover": {
        borderColor: "primary.main",
        bgcolor: "action.hover",
      },
    }}
  >
    <Box sx={{ color: "primary.main", mt: 0.25, display: "flex" }}>{icon}</Box>
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
        {badge}
      </Box>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </ButtonBase>
);

const LegacyChip = () => (
  <Chip label="Legacy" size="small" variant="outlined" sx={{ height: 18 }} />
);

export const ScoringWizardDialog: FC<Props> = ({
  open,
  onClose,
  onCreateWithAi,
  onBuildManually,
  onGenerated,
  segments = [],
}) => {
  const [step, setStep] = useState<WizardStep>("choose");
  const [inputMode, setInputMode] = useState<InputMode>("products");
  const [productIds, setProductIds] = useState<number[]>([]);
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [generateProfile, { loading }] = useGenerateScoringProfile();

  const resetState = useCallback(() => {
    setStep("choose");
    setInputMode("products");
    setProductIds([]);
    setSelectedSegment(null);
    setError(null);
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleCreateWithAi = () => {
    handleClose();
    onCreateWithAi();
  };

  const handleGenerate = async () => {
    setStep("building");
    setError(null);

    try {
      const variables: { productIds: number[] } | { companiesFilter: string } =
        inputMode === "products"
          ? { productIds }
          : {
              companiesFilter: JSON.stringify({
                filters:
                  safeParseFilters(selectedSegment?.metadata?.filters) ?? {},
                localizeInstalls: true,
              }),
            };

      const { data } = await generateProfile({ variables });
      const rawCriteria = data?.generatedScoring?.parameters?.criteria ?? [];
      const criteria = transformGeneratedProfile(rawCriteria);

      if (criteria.length === 0 || isEmptyScoringTypeReturned(criteria)) {
        setError(
          "Not enough data to generate a profile. You can still build one manually."
        );
        setStep("input");
        return;
      }

      // Build suggested name
      const name =
        inputMode === "segment" && selectedSegment?.name
          ? `Generated — ${selectedSegment.name}`
          : `Generated — ${productIds.length} product${productIds.length !== 1 ? "s" : ""}`;

      handleClose();
      onGenerated(criteria, name);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to generate scoring profile. Please try again."
      );
      setStep("input");
    }
  };

  const canProceed =
    inputMode === "products" ? productIds.length > 0 : selectedSegment !== null;

  // The AI card authors an argo model, which MA only renders for tenants running
  // V2 scoring — offering it without the flag would produce a model they can't
  // use, so hide it rather than disable it (no point advertising it).
  const scoringV2Enabled = useFeatureFlag("maScoringModelsV2", false);

  // Authoring an AI model lives in the admin app. Checking the URL up front (with
  // the same builder the click uses) keeps the card's enabled state honest instead
  // of letting the click silently no-op where ADMIN_URL isn't set.
  const aiScoringAvailable = getAiScoringUrl(getTenantId()) !== null;

  return (
    <Dialog
      open={open}
      onClose={step === "building" ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={step === "building"}
      TransitionProps={{ onExited: resetState }}
    >
      <DialogTitle>New Scoring Profile</DialogTitle>

      <DialogContent sx={{ minHeight: 300 }}>
        {/* ── CHOOSE step ──────────────────────────────────────────── */}
        {step === "choose" && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              How would you like to create this scoring profile?
            </Typography>

            {scoringV2Enabled && (
              <Tooltip
                title={
                  aiScoringAvailable
                    ? ""
                    : "AI-assisted scoring isn't available in this environment."
                }
              >
                {/* wrapper keeps the tooltip working while the card is disabled */}
                <Box component="span" sx={{ display: "block", width: "100%" }}>
                  <MethodCard
                    icon={<AutoAwesomeIcon sx={{ fontSize: 36 }} />}
                    title="AI-assisted outbound scoring"
                    description="Our AI agent researches your market and automatically builds the best scoring model for you."
                    badge={<AiBadge label="New" />}
                    testId="scoring-ai-assisted-btn"
                    disabled={!aiScoringAvailable}
                    onClick={handleCreateWithAi}
                  />
                </Box>
              </Tooltip>
            )}

            <MethodCard
              icon={<AutoFixHighOutlinedIcon sx={{ fontSize: 36 }} />}
              title="Quick start account scoring"
              description="Specify a set of companies (existing customers, competitors, etc.) for us to analyze. We will generate an account scoring profile based on their shared attributes."
              badge={<LegacyChip />}
              testId="scoring-quick-start-btn"
              onClick={() => setStep("input")}
            />

            <MethodCard
              icon={<ListAltOutlinedIcon sx={{ fontSize: 36 }} />}
              title="Manual account scoring"
              description="Define your custom scoring and weighting individually, by scoring type."
              badge={<LegacyChip />}
              testId="scoring-manual-btn"
              onClick={onBuildManually}
              last
            />
          </>
        )}

        {/* ── INPUT step ───────────────────────────────────────────── */}
        {step === "input" && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              How would you like to specify a company list for us to analyze? We
              will stack rank their commonalities to create a scoring profile.
            </Typography>

            <RadioGroup
              value={inputMode}
              onChange={(e) => setInputMode(e.target.value as InputMode)}
              sx={{ mb: 2 }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid",
                    borderColor:
                      inputMode === "products" ? "primary.main" : "divider",
                    borderRadius: 1,
                    p: 1.5,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    bgcolor:
                      inputMode === "products" ? "action.hover" : "transparent",
                    cursor: "pointer",
                  }}
                  onClick={() => setInputMode("products")}
                >
                  <Radio
                    checked={inputMode === "products"}
                    value="products"
                    size="small"
                    sx={{ p: 0.5 }}
                  />
                  <SearchIcon
                    sx={{ color: "primary.main", fontSize: 32, mt: 0.25 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Search for Product
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Specify companies that have the following product installs
                      detected
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid",
                    borderColor:
                      inputMode === "segment" ? "primary.main" : "divider",
                    borderRadius: 1,
                    p: 1.5,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    bgcolor:
                      inputMode === "segment" ? "action.hover" : "transparent",
                    cursor: "pointer",
                  }}
                  onClick={() => setInputMode("segment")}
                >
                  <Radio
                    data-testid="scoring-segment-mode-radio"
                    checked={inputMode === "segment"}
                    value="segment"
                    size="small"
                    sx={{ p: 0.5 }}
                  />
                  <ListIcon
                    sx={{ color: "primary.main", fontSize: 32, mt: 0.25 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Choose from List
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create a profile based on companies from your saved
                      segments
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </RadioGroup>

            {inputMode === "products" && (
              <ProductScoringSection
                productIds={productIds}
                onProductsChange={setProductIds}
                grouped
              />
            )}

            {inputMode === "segment" &&
              (segments.length === 0 ? (
                <Alert severity="warning">
                  Looks like you do not have any saved segments. You can create
                  a new segment by closing this window and applying filters.
                </Alert>
              ) : (
                <Autocomplete<Segment>
                  options={segments}
                  getOptionLabel={(s) => s.name ?? "Unnamed segment"}
                  value={selectedSegment}
                  onChange={(_, val) => setSelectedSegment(val)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Select a segment"
                      placeholder="Search segments"
                      inputProps={{
                        ...params.inputProps,
                        "data-testid": "scoring-segment-select-input",
                      }}
                    />
                  )}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                />
              ))}

            {error && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}

        {/* ── BUILDING step ────────────────────────────────────────── */}
        {step === "building" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 2,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="h6" fontWeight={700}>
              Building...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please do not close this window or refresh your browser until the
              build is complete.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === "choose" && (
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
        )}

        {step === "input" && (
          <>
            <Button
              onClick={() => {
                setStep("choose");
                setError(null);
              }}
              startIcon={<ArrowBackIcon />}
            >
              BACK
            </Button>
            <Button
              variant="contained"
              disabled={!canProceed || loading}
              onClick={handleGenerate}
              endIcon={<ArrowForwardIcon />}
              data-testid="scoring-quick-start-next-btn"
            >
              NEXT
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
